import { yupResolver } from '@hookform/resolvers/yup'
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { ReactNode, useState } from 'react'
import { useForm } from 'react-hook-form'
import { number, object, string } from 'yup'
import { MonitorMode } from '../../api/client'
import Button from '../Button'
import ApifyResourcesHelp from './ApifyResourcesHelp'
import { PlatformConfig } from './platforms'
import { ALL_PROFILES, STANDARD_PROFILE, hintFor } from './scrape-profiles'

/** What the form collects, ready for `createMonitorProfileSchedule`. */
export type MonitorScheduleValues = {
    mode: MonitorMode
    windowDays?: number
    targets?: string[]
    since?: string
    cron: string
    /** Absent when it is the standard budget, which the backend defaults to. */
    scrapeProfile?: string
}

type Props = {
    platform: PlatformConfig
    /** The account field, when the caller has one to choose. */
    accountField?: ReactNode
    /** Whether an account has been settled on, wherever it came from. */
    hasAccount: boolean
    submitLabel: string
    submitting: boolean
    /** Show the choices without allowing them, e.g. on an authorised account. */
    disabled?: boolean
    onSubmit: (values: MonitorScheduleValues) => void
    onCancel?: () => void
}

interface FormData {
    /** Nullable to match yup's notRequired() inference. */
    windowDays?: number | null
    targets?: string
    since?: string
    time?: string
    cron?: string
}

function validationSchema(platform: PlatformConfig, mode: MonitorMode, useRawCron: boolean) {
    const postsMode = mode === MonitorMode.Posts
    const needsSince = postsMode && platform.postsModeNeedsSinceDate
    return object({
        windowDays: number()
            .transform((value, originalValue) =>
                originalValue === '' || originalValue === null ? undefined : value
            )
            .typeError('Window must be a number of days')
            .integer('Window must be a whole number of days')
            .min(1, 'Window must be at least 1 day')
            .notRequired(),
        targets: postsMode
            ? string()
                  .trim()
                  .required(`${platform.targetsLabel} are required in specific-posts mode`)
            : string(),
        since: needsSince
            ? string().required('Campaign start date is required in specific-posts mode')
            : string(),
        time: useRawCron ? string() : string().required('A time of day is required'),
        cron: useRawCron
            ? string()
                  .trim()
                  .required('Cron expression is required')
                  .matches(
                      /^\S+(\s+\S+){5}$/,
                      'Seconds-first cron has six fields, e.g. 0 0 6 * * *'
                  )
            : string()
    })
}

/** E.g. "06:30" → "0 30 6 * * *". */
function composeDailyCron(time: string): string {
    const [hours, minutes] = time.split(':')
    return `0 ${parseInt(minutes, 10)} ${parseInt(hours, 10)} * * *`
}

export function splitTargets(text: string): string[] {
    return text
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
}

/**
 * The monitoring schedule form: what to watch, how far back, when it runs,
 * and how much Apify resource each run gets.
 *
 * Shared by the admin create page and a profile's own settings, which
 * differ only in whether the account is chosen or already known.
 */
function MonitorScheduleForm({
    platform,
    accountField,
    hasAccount,
    submitLabel,
    submitting,
    disabled = false,
    onSubmit,
    onCancel
}: Props) {
    const [mode, setMode] = useState<MonitorMode>(
        platform.supportsProfileMode ? MonitorMode.Profile : MonitorMode.Posts
    )
    const [useRawCron, setUseRawCron] = useState(false)
    const [showAdvanced, setShowAdvanced] = useState(false)
    const [scrapeProfile, setScrapeProfile] = useState<string>(STANDARD_PROFILE.wireValue)

    const form = useForm<FormData>({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(validationSchema(platform, mode, useRawCron)),
        defaultValues: { targets: '', since: '', time: '06:00', cron: '0 0 6 * * *' }
    })

    const labelClasses = 'block text-sm font-medium text-ink-700'
    const inputClasses =
        'mt-1 block w-full border border-ink-300 rounded-md shadow-sm text-sm focus:ring-arwen-500 focus:border-arwen-500'
    const hintClasses = 'mt-1 text-xs text-ink-500'
    const errorClasses = 'mt-1 text-xs text-severe-600'

    const submit = form.handleSubmit((data) => {
        const postsMode = mode === MonitorMode.Posts
        onSubmit({
            mode,
            windowDays: postsMode ? undefined : (data.windowDays ?? undefined) || undefined,
            targets: postsMode ? splitTargets(data.targets ?? '') : undefined,
            since:
                postsMode && platform.postsModeNeedsSinceDate ? data.since || undefined : undefined,
            cron: useRawCron ? (data.cron ?? '').trim() : composeDailyCron(data.time ?? '06:00'),
            scrapeProfile: scrapeProfile === STANDARD_PROFILE.wireValue ? undefined : scrapeProfile
        })
    })

    return (
        <form
            onSubmit={(event) => {
                event.preventDefault()
                submit()
            }}
            className="space-y-4"
        >
            {accountField}

            <fieldset>
                <legend className={labelClasses}>What to monitor</legend>
                <div className="mt-2 space-y-2">
                    <label
                        className={`flex items-start gap-2 text-sm ${
                            platform.supportsProfileMode ? 'text-ink-900' : 'text-ink-400'
                        }`}
                    >
                        <input
                            type="radio"
                            name="mode"
                            className="mt-0.5 border-ink-300 text-arwen-600 focus:ring-arwen-500"
                            checked={mode === MonitorMode.Profile}
                            disabled={disabled || !platform.supportsProfileMode}
                            onChange={() => setMode(MonitorMode.Profile)}
                        />
                        <span>
                            <span className="font-medium">Whole profile</span> — each run discovers
                            the account&apos;s recent posts and collects their comments.
                        </span>
                    </label>
                    {!platform.supportsProfileMode ? (
                        <p className={`${hintClasses} ml-6`}>
                            A YouTube channel with Fetch with API on is polled for uploads and its
                            comments collected by the rotation, so it needs no schedule. Schedules
                            are for specific videos on a channel with the switch off.
                        </p>
                    ) : null}
                    <label
                        className={`flex items-start gap-2 text-sm ${
                            platform.supportsPostsMode ? 'text-ink-900' : 'text-ink-400'
                        }`}
                    >
                        <input
                            type="radio"
                            name="mode"
                            className="mt-0.5 border-ink-300 text-arwen-600 focus:ring-arwen-500"
                            checked={mode === MonitorMode.Posts}
                            disabled={disabled || !platform.supportsPostsMode}
                            onChange={() => setMode(MonitorMode.Posts)}
                        />
                        <span>
                            <span className="font-medium">Specific posts</span> — each run collects
                            comments on a fixed list of posts.
                        </span>
                    </label>
                    {!platform.supportsPostsMode ? (
                        <p className={`${hintClasses} ml-6`}>
                            Targeted comment scraping is not supported for Facebook — only
                            whole-profile monitoring.
                        </p>
                    ) : null}
                </div>
            </fieldset>

            {mode === MonitorMode.Profile ? (
                <div>
                    <label className={labelClasses} htmlFor="windowDays">
                        Window (days)
                    </label>
                    <input
                        id="windowDays"
                        type="number"
                        min={1}
                        placeholder="2"
                        className={inputClasses}
                        {...form.register('windowDays')}
                        disabled={disabled}
                    />
                    <p className={errorClasses}>{form.formState.errors.windowDays?.message}</p>
                    <p className={hintClasses}>
                        A rolling window: each run collects comments from posts made in the last N
                        days, resolved at run time. Leave blank for the default of 2 days.
                    </p>
                </div>
            ) : (
                <>
                    <div>
                        <label className={labelClasses} htmlFor="targets">
                            {platform.targetsLabel}
                        </label>
                        <textarea
                            id="targets"
                            rows={5}
                            className={inputClasses}
                            {...form.register('targets')}
                            disabled={disabled}
                        />
                        <p className={errorClasses}>{form.formState.errors.targets?.message}</p>
                        <p className={hintClasses}>{platform.targetsHint}</p>
                    </div>
                    {platform.postsModeNeedsSinceDate ? (
                        <div>
                            <label className={labelClasses} htmlFor="since">
                                Collect comments since
                            </label>
                            <input
                                id="since"
                                type="date"
                                className={inputClasses}
                                {...form.register('since')}
                                disabled={disabled}
                            />
                            <p className={errorClasses}>{form.formState.errors.since?.message}</p>
                            <p className={hintClasses}>
                                The campaign start date (UTC). Kept fixed rather than rolling, so
                                posts added mid-campaign keep their older comments.
                            </p>
                        </div>
                    ) : (
                        <p className={hintClasses}>
                            YouTube&apos;s targeted fetch always collects each video&apos;s full
                            comment set, so no date window applies.
                        </p>
                    )}
                </>
            )}

            {!useRawCron ? (
                <div>
                    <label className={labelClasses} htmlFor="time">
                        Runs daily at (UTC)
                    </label>
                    <input
                        id="time"
                        type="time"
                        className={inputClasses}
                        {...form.register('time')}
                        disabled={disabled}
                    />
                    <p className={errorClasses}>{form.formState.errors.time?.message}</p>
                </div>
            ) : (
                <div>
                    <label className={labelClasses} htmlFor="cron">
                        Cron expression
                    </label>
                    <input
                        id="cron"
                        type="text"
                        className={inputClasses}
                        placeholder="0 0 6 * * *"
                        {...form.register('cron')}
                        disabled={disabled}
                    />
                    <p className={errorClasses}>{form.formState.errors.cron?.message}</p>
                    <p className={hintClasses}>
                        Seconds-first cron with six fields: second minute hour day-of-month month
                        day-of-week. For example 0 0 6 * * * runs daily at 06:00 UTC.
                    </p>
                </div>
            )}
            <label className="flex items-center gap-2 text-sm text-ink-700">
                <input
                    type="checkbox"
                    className="rounded border-ink-300 text-arwen-600 focus:ring-arwen-500"
                    checked={useRawCron}
                    disabled={disabled}
                    onChange={(e) => setUseRawCron(e.target.checked)}
                />
                Use a custom cron expression
            </label>

            <div className="pt-2 border-t border-ink-200">
                <button
                    type="button"
                    className="flex items-center gap-1 text-sm font-medium text-ink-700 hover:text-ink-900 focus:outline-none focus:ring-2 focus:ring-arwen-500 rounded"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    aria-expanded={showAdvanced}
                >
                    {showAdvanced ? (
                        <ChevronDownIcon className="h-4 w-4" />
                    ) : (
                        <ChevronRightIcon className="h-4 w-4" />
                    )}
                    Advanced
                </button>
                {showAdvanced ? (
                    <div className="mt-3">
                        <label className={labelClasses} htmlFor="scrapeProfile">
                            Apify resources
                        </label>
                        <div className="mt-1">
                            <ApifyResourcesHelp />
                        </div>
                        <select
                            id="scrapeProfile"
                            className={inputClasses}
                            value={scrapeProfile}
                            disabled={disabled}
                            onChange={(e) => setScrapeProfile(e.target.value)}
                        >
                            {ALL_PROFILES.map((profile) => (
                                <option key={profile.wireValue} value={profile.wireValue}>
                                    {profile.label}
                                </option>
                            ))}
                        </select>
                        <p className={hintClasses}>{hintFor(scrapeProfile)}</p>
                    </div>
                ) : null}
            </div>

            <div className="flex items-center gap-3">
                <Button
                    disabled={disabled || submitting || !hasAccount}
                    loading={submitting}
                    primary={true}
                    text={submitLabel}
                    type="submit"
                    size="medium"
                />
                {onCancel ? (
                    <Button
                        primary={false}
                        text="Cancel"
                        type="button"
                        size="medium"
                        onClick={onCancel}
                    />
                ) : null}
            </div>
        </form>
    )
}

export default MonitorScheduleForm
